import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { formatDistanceToNow } from 'date-fns'
import { createId } from '@paralleldrive/cuid2'

import { env } from '@/env.mjs'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(input: string | number): string {
  const date = new Date(input)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

export function formatDateAndTime(input: string | number): string {
  const date = new Date(input)
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })
  return `${formattedDate}, ${formattedTime}`
}

export function formatRelativeTime(input: string | number): string {
  const date = new Date(input)
  const now = new Date()

  // Debug: Log the dates to see what's happening
  console.log('Input time:', input)
  console.log('Parsed date:', date.toISOString())
  console.log('Current time:', now.toISOString())
  console.log('Time difference (ms):', now.getTime() - date.getTime())

  // TEMPORARY WORKAROUND: Backend is storing times 7 hours in the future
  // Subtract 7 hours (25200000 ms) from the backend timestamp to get actual time
  const correctedDate = new Date(date.getTime() - 7 * 60 * 60 * 1000)
  console.log('Corrected date:', correctedDate.toISOString())
  console.log('Corrected time difference (ms):', now.getTime() - correctedDate.getTime())

  return formatDistanceToNow(correctedDate, { addSuffix: true })
}

export function absoluteUrl(path: string) {
  return `${env.NEXT_PUBLIC_APP_URL}${path}`
}

export function capFirstLetter(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export function toSentenceCase(str: string) {
  return str.toLowerCase().replace(/\w\S*/g, txt => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const stringWithContent = (
  message?: string,
  isOptional = false
): [(v: string | null | undefined) => boolean, string] => [
  (v: string | null | undefined) =>
    isOptional
      ? (typeof v === 'string' && v !== '') || v
        ? v.trim().length > 0
        : true
      : typeof v === 'string' || v
        ? v.trim().length > 0
        : true,
  message || 'Cannot be empty or contain only whitespace characters'
]

export function formatRelativeTimeCompact(input: string | number): string {
  const date = new Date(input)
  const now = new Date()

  // Apply the same 7-hour correction as the regular formatRelativeTime
  const correctedDate = new Date(date.getTime() - 7 * 60 * 60 * 1000)
  const diffMs = now.getTime() - correctedDate.getTime()

  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffYears > 0) return `${diffYears}y`
  if (diffMonths > 0) return `${diffMonths}mo`
  if (diffWeeks > 0) return `${diffWeeks}w`
  if (diffDays > 0) return `${diffDays}d`
  if (diffHours > 0) return `${diffHours}h`
  if (diffMinutes > 0) return `${diffMinutes}m`
  return `${diffSeconds}s`
}

/**
 * Generate a unique conversation ID using CUID2
 * This ID is used for both text chat and voice sessions
 */
export function generateConversationId(): string {
  return createId()
}
